import React, { useEffect, useState } from "react";
import { Col, Collapse, FormGroup, Input, Label, Row } from "reactstrap";
import { stateOptions } from "../../../../helpers/StateOptions";
import SearchableDropdown from "../../../../helpers/Common/SearchableDropdown";
import MultiSelector from "../../../../components/MultiSelector";
import { toArray } from "../../../../helpers/common";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const AddMoreDetails = ({ formData, setFormData, onChangeValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const [isPresentAddressSame, setIsPresentAddressSame] = useState(false);

  useEffect(() => {
    if (isPresentAddressSame) {
      setFormData((prev) => ({
        ...prev,
        present_address: prev.permanentAddress || prev.permanent_address,
        same_as_permanent_address: "Yes",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        same_as_permanent_address: "No",
      }));
    }
  }, [isPresentAddressSame, setFormData]);

  useEffect(() => {
    if (formData?.same_as_permanent_address === "Yes") {
      setIsPresentAddressSame(true);
    }
  }, [formData?.same_as_permanent_address]);

  const parseToArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      const trimmed = raw.trim().toLowerCase();
      if (!trimmed || trimmed === "string" || trimmed === "[]") return [];
      return toArray(raw);
    }
    return [];
  };

  const handleMultiInputChange = (values, field) => {
    // Keep as string formatted for payload, but handle array/string normalization
    const formattedValue = Array.isArray(values) ? values.join(", ") : values;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: formattedValue,
      };

      // 🟢 FIX: Sync both camelCase and lowercase key names for languages
      if (field === "languagesKnow" || field === "languagesknow") {
        updated.languagesKnow = formattedValue;
        updated.languagesknow = formattedValue;
        updated.languagesKnown = formattedValue;
      }

      return updated;
    });
  };

  return (
    <div className="w-100">
      <div className="m-auto text-end">
        <button
          className="btn btn-link my-link-btn text-underline"
          type="button"
          onClick={toggle}
        >
          {isOpen ? "Hide more details" : "Show more details"}
        </button>
      </div>

      <Collapse isOpen={isOpen}>
        <Row className="mt-2">
          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="introduce-yourself" className="form-label">
                Introduce Yourself
              </Label>

              <CKEditor
                editor={ClassicEditor}
                data={formData.details || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setFormData((prev) => ({ ...prev, details: data }));
                  if (onChangeValue) onChangeValue(data, "details");
                }}
              />
            </div>
          </Col>

          {/* 🟢 FIX: Correct fallbacks for language state */}
          <MultiSelector
            initialItems={parseToArray(
              formData?.languagesknow ??
                formData?.languagesKnow ??
                formData?.languagesKnown
            )}
            onChange={(values) =>
              handleMultiInputChange(values, "languagesknow")
            }
            maxItems={20}
            label="Languages Known (separate each with a comma)"
            placeholder="e.g. English, Hindi, Urdu"
            disableOnMaxItems={true}
          />

          {/* Skills Field */}
          <MultiSelector
            initialItems={parseToArray(formData?.skills)}
            onChange={(values) => handleMultiInputChange(values, "skills")}
            maxItems={20}
            placeholder="Eg. JavaScript, React, Node.js"
            disableOnMaxItems={true}
            label="Skills (separate each with a comma)"
          />

          {/* Hobbies Field */}
          <MultiSelector
            initialItems={parseToArray(formData?.hobbies)}
            onChange={(values) => handleMultiInputChange(values, "hobbies")}
            maxItems={20}
            placeholder="Enter hobbies (e.g., Reading, Hiking)"
            disableOnMaxItems={true}
            label="Hobbies (separate each with a comma)"
          />

          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="permanent_address" className="form-label">
                Permanent Address (Home Town)
              </Label>
              <textarea
                className="form-control"
                rows="3"
                id="permanent_address"
                placeholder="Permanent Address (Home Town)"
                value={
                  formData.permanentAddress || formData.permanent_address || ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    permanentAddress: val,
                    permanent_address: val,
                  }));
                  if (onChangeValue) onChangeValue(e, "permanentAddress");
                }}
              />
            </div>
          </Col>

          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="choices-single-state" className="form-label">
                Your State
              </Label>
              <SearchableDropdown
                placeholder="Select Your State"
                options={stateOptions}
                value={formData.state}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, state: value?.toString() }))
                }
              />
            </div>
          </Col>
        </Row>

        <div className="mt-3">
          <div className="cust-divider"></div>

          <h5 className="fs-17 fw-semibold mb-3">Social Media</h5>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="facebook" className="form-label">
                  Facebook
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="facebook"
                  placeholder="Enter your facebook url"
                  value={formData.facebookUrl || formData.facebook_url || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      facebookUrl: val,
                      facebook_url: val,
                    }));
                  }}
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="twitter" className="form-label">
                  Instagram
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="twitter"
                  placeholder="Enter your Instagram url"
                  value={formData.twitterUrl || formData.twitter_url || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      twitterUrl: val,
                      twitter_url: val,
                    }));
                  }}
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="linkedin" className="form-label">
                  Linkedin
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="linkedin"
                  placeholder="Enter your linkedin url"
                  value={formData.linkedinUrl || formData.linkedin_url || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      linkedinUrl: val,
                      linkedin_url: val,
                    }));
                  }}
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="whatsapp" className="form-label">
                  Whatsapp
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  id="whatsapp"
                  placeholder="Enter your whatsapp number"
                  value={
                    formData.whatsappNumber || formData.whatsapp_number || ""
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      whatsappNumber: val,
                      whatsapp_number: val,
                    }));
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </Collapse>
    </div>
  );
};

export default AddMoreDetails;